import { Component, computed, input, output, signal } from '@angular/core';
import { InfoText, InlineNotificationType, TextNodeType } from '@mwe/models';
import { HeadlineService } from '../headline/headline.service';
import { NgClass } from '@angular/common';
import { HeadlineComponent } from '../headline/headline.component';
import { InfoTextIconComponent } from '../info-text/info-text-icon.component';
import { TranslatePipe } from '@ngx-translate/core';
import { SafeHtmlPipe } from '@mwe/ui/pipes';
import { ButtonLinkComponent } from '../buttons/button-link/button-link.component';
import ShortUniqueId from 'short-unique-id';

const ColorSchemes = {
  success: {
    borderClass: 'border-success',
    backgroundClass: 'bg-green-010',
    iconColorClass: 'text-success',
  },
  danger: {
    borderClass: 'border-danger',
    backgroundClass: 'bg-red-050',
    iconColorClass: 'text-danger',
  },
  info: {
    borderClass: 'border-secondary-light',
    backgroundClass: 'bg-blue-050',
    iconColorClass: 'text-secondary-light',
  },
  warning: {
    borderClass: 'border-warning',
    backgroundClass: 'bg-yellow-050',
    iconColorClass: 'text-warning',
  },
  neutral: {
    borderClass: 'border-neutral-400',
    backgroundClass: 'bg-neutral-010',
    iconColorClass: 'border-neutral-400',
  },
  transparent: {
    borderClass: 'border-neutral-400',
    backgroundClass: '',
    iconColorClass: 'border-neutral-400',
  },
};

@Component({
  selector: 'mwe-infobox',
  templateUrl: './infobox.component.html',
  providers: [HeadlineService],
  imports: [NgClass, HeadlineComponent, InfoTextIconComponent, TranslatePipe, SafeHtmlPipe, ButtonLinkComponent],
})
export class InfoboxComponent {
  readonly onLinkClick = output<void>();
  readonly onCTAClick = output<void>();

  readonly type = input<InlineNotificationType>('info');
  readonly titleKey = input<string>();
  readonly messageKey = input<string>();
  readonly messageKeys = input<string[]>();
  readonly textParams = input<unknown>({}); // this seems to be used differently mainly Map / Record / { key: value }
  readonly link = input<string>();
  readonly iconClassOverwrite = input<string>('');
  readonly infoText = input<InfoText>();
  readonly headingNodeType = input<TextNodeType>('h5');
  readonly ctaLabelKey = input<string>();
  readonly ctaPrependIconClass = input<string>();
  readonly ctaWrapperClass = input<string>('text-end');

  readonly isVisible = signal<boolean>(true);
  readonly uuid = signal<string>(new ShortUniqueId({ dictionary: 'alpha' }).rnd(8));
  readonly colorScheme = computed(() => ColorSchemes[this.type()]);
  readonly showTitle = computed<boolean>(() => this.titleKey() && this.titleKey().length > 0);

  readonly textParamsAsRecord = computed<Record<string, unknown>>(() => {
    // translation only works for Records
    const textParams = this.textParams();

    // Map
    if (textParams instanceof Map) {
      return Object.fromEntries(textParams);
    }

    // Record or key-value-pairs like { key: value }
    return textParams;
  });

  readonly CssClasses = computed<string[]>(() => {
    const classes = [];

    classes.push(this.colorScheme().borderClass);
    classes.push(this.colorScheme().backgroundClass);

    if (!this.isVisible()) {
      classes.push('d-none');
    }

    return classes;
  });

  readonly iconClasses = computed<string>(() => {
    if (this.iconClassOverwrite()) {
      return this.iconClassOverwrite();
    }

    switch (this.type()) {
      case 'danger':
        return 'fas fa-circle-xmark';
      case 'warning':
        return 'fas fa-circle-exclamation';
      case 'success':
        return 'fas fa-circle-check';
      default:
        return 'fas fa-circle-info';
    }
  });
}
